import {
  Component,
  Input,
  OnInit,
  Output,
  OnDestroy,
  EventEmitter,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import moment, { Moment } from 'moment';
import { Subscription } from 'rxjs';
import {
  CalendarOptionValue,
  CalendarRangeOutput,
  CalendarRangeType,
} from 'src/shared/models';

@Component({
  selector: 'organism-calendar-filter',
  templateUrl: './calendar-filter.component.html',
  styleUrls: ['./calendar-filter.component.scss'],
})
export class CalendarFilterComponent implements OnInit, OnDestroy {
  @Input() isGrid: boolean = false;
  @Input() enabledOptions: CalendarOptionValue[] | null = null;
  @Output() rangeChange = new EventEmitter<CalendarRangeOutput | null>();
  @ViewChild('checkboxGroup') checkboxGroup: any;

  calendarOptions: { title: string; value: CalendarOptionValue }[] = [];
  selectedCalendarType: CalendarOptionValue | null = null;
  selectedDateRange: CalendarRangeOutput | null = null;

  private langChangeSubscription: Subscription;
  private readonly defaultOptions: CalendarOptionValue[] = [
    CalendarOptionValue.SEMANA_ANTERIOR,
    CalendarOptionValue.MES_ACTUAL,
    CalendarOptionValue.MES_ANTERIOR,
    CalendarOptionValue.ANIO_ACTUAL,
    CalendarOptionValue.RANGO_DIAS,
    CalendarOptionValue.RANGO_MESES,
  ];

  private readonly CALENDAR_OPTIONS_CATALOG: {
    key: string;
    value: CalendarOptionValue;
  }[] = [
    {
      key: 'CALENDAR_FILTER.PREVIOUS_WEEK',
      value: CalendarOptionValue.SEMANA_ANTERIOR,
    },
    {
      key: 'CALENDAR_FILTER.CURRENT_MONTH',
      value: CalendarOptionValue.MES_ACTUAL,
    },
    {
      key: 'CALENDAR_FILTER.PREVIOUS_MONTH',
      value: CalendarOptionValue.MES_ANTERIOR,
    },
    {
      key: 'CALENDAR_FILTER.CURRENT_YEAR',
      value: CalendarOptionValue.ANIO_ACTUAL,
    },
    {
      key: 'CALENDAR_FILTER.RANGE_DAYS',
      value: CalendarOptionValue.RANGO_DIAS,
    },
    {
      key: 'CALENDAR_FILTER.RANGE_MONTHS',
      value: CalendarOptionValue.RANGO_MESES,
    },
    {
      key: 'CALENDAR_FILTER.PREVIOUS_DAY',
      value: CalendarOptionValue.DIA_ANTERIOR,
    },
    {
      key: 'CALENDAR_FILTER.SPECIFIC_DAY',
      value: CalendarOptionValue.DIA_ESPECIFICO,
    },
  ];

  constructor(private translateService: TranslateService) {
    // Suscribirse a los cambios de idioma
    this.langChangeSubscription = this.translateService.onLangChange.subscribe(
      () => {
        this.loadCalendarOptions();
      },
    );
  }

  ngOnInit() {
    this.loadCalendarOptions();
  }

  ngOnDestroy() {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }

  reset(): void {
    this.selectedCalendarType = null;
    this.selectedDateRange = null;
    this.rangeChange.emit(null);

    // Reset del checkbox group
    if (this.checkboxGroup && typeof this.checkboxGroup.reset === 'function') {
      this.checkboxGroup.reset();
    }
  }

  private loadCalendarOptions() {
    const optionsToRender =
      this.enabledOptions && this.enabledOptions.length > 0
        ? this.enabledOptions
        : this.defaultOptions;

    const filteredOptions = this.CALENDAR_OPTIONS_CATALOG.filter((option) =>
      optionsToRender.includes(option.value),
    );

    const translationKeys = filteredOptions.map((option) => option.key);

    this.translateService.get(translationKeys).subscribe((translations) => {
      this.calendarOptions = filteredOptions.map((option) => ({
        title: translations[option.key],
        value: option.value,
      }));
    });
  }

  get checkboxClass() {
    return this.isGrid
      ? 'calendar-filter__container-group__checkbox-grid'
      : 'calendar-filter__container-group__checkbox';
  }

  get calendarClass() {
    return this.isGrid ? 'calendar-container__modifier' : 'calendar-container';
  }

  onCalendarTypeChange(event: { value: string; checked: boolean }) {
    if (!this.isCalendarOption(event.value)) return;

    if (event.checked) {
      const option = event.value;
      this.selectedCalendarType = option;
      this.selectedDateRange = null;

      switch (this.selectedCalendarType) {
        case CalendarOptionValue.ANIO_ANTERIOR:
          this.setLastYearRange();
          break;
        case CalendarOptionValue.ANIO_ACTUAL:
          this.setCurrentYearRange();
          break;
        case CalendarOptionValue.MES_ACTUAL:
          this.setCurrentMonthRange();
          break;
        case CalendarOptionValue.MES_ANTERIOR:
          this.setLastMonthRange();
          break;
        case CalendarOptionValue.SEMANA_ANTERIOR:
          this.setPreviousWeek();
          break;
        case CalendarOptionValue.DIA_ANTERIOR:
          this.setPreviousDay();
          break;
      }
    }
  }

  onDateRangeSelected(range: { start: Date | null; end: Date | null }) {
    if (range.start && range.end) {
      // Validar que la fecha inicial no sea mayor que la final
      if (range.start > range.end) {
        // Limpiar selección si el rango es inválido
        this.selectedDateRange = null;
        this.rangeChange.emit(null);
        // Opcional: podrías mostrar un mensaje de error aquí
        return;
      }
      if (this.selectedCalendarType === CalendarOptionValue.RANGO_DIAS) {
        this.selectedDateRange = this.processDaysRange(range.start, range.end);
      } else if (
        this.selectedCalendarType === CalendarOptionValue.RANGO_MESES
      ) {
        this.selectedDateRange = this.processMonthsRange(
          range.start,
          range.end,
        );
      }

      this.processDateRange(this.selectedDateRange);
    } else {
      // Si el usuario comienza una nueva selección, limpiar la selección previa
      this.selectedDateRange = null;
      this.rangeChange.emit(null);
    }
  }

  onDateRangeChange(range: { startDate: string; endDate: string }) {
    if (range.startDate && range.endDate) {
      // Convertir las fechas string a Date objects
      const startDate = moment(range.startDate, 'DD/MM/YYYY').toDate();
      const endDate = moment(range.endDate, 'DD/MM/YYYY').toDate();

      if (this.selectedCalendarType === CalendarOptionValue.RANGO_DIAS) {
        this.selectedDateRange = this.processDaysRange(startDate, endDate);
        this.processDateRange(this.selectedDateRange);
      }
    }
  }

  onDateChange(date: Moment | null) {
    if (
      date &&
      this.selectedCalendarType === CalendarOptionValue.DIA_ESPECIFICO
    ) {
      this.selectedDateRange = {
        tipo: CalendarRangeType.DIA_ESPECIFICO,
        date: date.format('DD/MM/YYYY'),
      };
     
      this.processDateRange(this.selectedDateRange);
    }
  }

  private processDaysRange(start: Date, end: Date): CalendarRangeOutput {
    const startMoment = moment(start);
    const endMoment = moment(end);

    return {
      tipo: CalendarRangeType.RANGO_DIAS,
      fechaInicio: startMoment.format('DD/MM/YYYY'),
      fechaFin: endMoment.format('DD/MM/YYYY'),
    };
  }

  private processMonthsRange(start: Date, end: Date): CalendarRangeOutput {
    const startMoment = moment(start);
    const endMoment = moment(end);

    return {
      tipo: CalendarRangeType.RANGO_MESES,
      fechaInicio: startMoment.format('MM/YYYY'),
      fechaFin: endMoment.format('MM/YYYY'),
    };
  }

  private setLastYearRange() {
    const now = moment();
    const lastYear = now.clone().subtract(1, 'year');

    this.selectedDateRange = {
      tipo: CalendarRangeType.ANIO_ANTERIOR,
      date: lastYear.format('YYYY'),
    };

    this.processDateRange(this.selectedDateRange);
  }

  private setCurrentYearRange() {
    const now = moment();

    this.selectedDateRange = {
      tipo: CalendarRangeType.ANIO_ACTUAL,
      date: now.format('YYYY'),
    };

    this.processDateRange(this.selectedDateRange);
  }

  private setCurrentMonthRange() {
    const now = moment();

    this.selectedDateRange = {
      tipo: CalendarRangeType.MES_ACTUAL,
      date: now.format('MM/YYYY'),
    };

    this.processDateRange(this.selectedDateRange);
  }

  private setLastMonthRange() {
    const now = moment();
    const lastMonth = now.clone().subtract(1, 'month');

    this.selectedDateRange = {
      tipo: CalendarRangeType.MES_ANTERIOR,
      date: lastMonth.format('MM/YYYY'),
    };

    this.processDateRange(this.selectedDateRange);
  }

  private setPreviousWeek() {
    const now = moment();
    const startOfWeek = now.clone().subtract(1, 'week').startOf('week');
    const endOfWeek = now.clone().subtract(1, 'week').endOf('week');

    this.selectedDateRange = {
      tipo: CalendarRangeType.RANGO_DIAS,
      fechaInicio: startOfWeek.format('DD/MM/YYYY'),
      fechaFin: endOfWeek.format('DD/MM/YYYY'),
    };

    this.processDateRange(this.selectedDateRange);
  }

  private setPreviousDay() {
    const yesterday = moment().subtract(1, 'day');

    this.selectedDateRange = {
      tipo: CalendarRangeType.DIA_ANTERIOR,
      date: yesterday.format('DD/MM/YYYY'),
    };

    this.processDateRange(this.selectedDateRange);
  }

  private processDateRange(range: CalendarRangeOutput | null) {
    if (range) {
      this.rangeChange.emit(range);
    }
  }

  private isCalendarOption(value: string): value is CalendarOptionValue {
    return Object.values(CalendarOptionValue).includes(
      value as CalendarOptionValue,
    );
  }

  getSelectedRange(): CalendarRangeOutput | null {
    return this.selectedDateRange;
  }
}
